import { useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Dropdown, Input, Select, Table, Tooltip } from 'antd';
import type { MenuProps, TableProps } from 'antd';
import { MoreHorizontal, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  ACCESS_ROLES,
  OFFICER_SORT_FIELDS,
  type OfficerDto,
  type OfficerSortField,
  type Paginated,
} from '@pams/shared';
import { ElevationMark } from '@/components/elevation/ElevationMark';
import { EmptyState } from '@/components/EmptyState';
import { FilterField } from '@/components/FilterField';
import { OfficerAvatar } from '@/components/OfficerAvatar';
import { OrgUnitPicker } from '@/components/OrgUnitPicker';
import { NameCell } from '@/components/NameCell';
import { PageHeader } from '@/components/PageHeader';
import { RankPicker } from '@/components/RankPicker';
import { StatusDot } from '@/components/StatusDot';
import { OfficerDrawer } from './components/OfficerDrawer';
import { TransferOfficerModal } from './components/TransferOfficerModal';
import { useAuth } from '@/hooks/useAuth';
import { ColumnsMenu } from '@/components/ColumnsMenu';
import { columnKeySet } from '@/lib/tableColumns';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { useServerTable } from '@/hooks/useServerTable';
import {
  can,
  READ_ONLY_TOOLTIP,
  actingOrgUnitTypeOf,
  elevatedRefusalAt,
  superAdminPeerRefusal,
} from '@/lib/accessRole';
import { ACCESS_ROLE_LABEL, accessRoleLabel } from '@/lib/labels';
import { api } from '@/lib/api';
import { fetchScopedOrgTree } from '@/lib/orgTree';
import { queryKeys } from '@/lib/queryKeys';

/**
 * The officer list — the first consumer of `useServerTable`.
 *
 * Everything about paging, sorting, URL state and the debounce lives in the hook; this
 * page supplies the allowlist, the query function and the columns.
 *
 * The rows are already scoped by the API, so there is no client-side filtering here to
 * get wrong: a PS nodal simply receives fewer rows and a smaller `total`.
 *
 * ─── Row actions: two inline, the rest behind ⋯ ──────────────────────────────────────
 * `Edit` and `Transfer` stay as labelled controls rather than moving into the overflow.
 * Not a test-compatibility dodge — it is the reason those tests exist. A read-only nodal
 * under an overflow-only design sees an identical ⋯ on every row, clicks it, and finds
 * two greyed items; here they see the greyed controls immediately and learn their access
 * level from the table itself. "A control that vanishes generates a 'where did it go'
 * question" is the same rule `AppShell` states for nav, applied to row actions.
 *
 * The overflow carries the long tail, and its items must NEVER reuse the labels `Edit` or
 * `Transfer` — a duplicate accessible name makes the row-level queries ambiguous the
 * moment anyone opens the menu.
 */
const OfficersPage = () => {
  const { officer } = useAuth();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [editing, setEditing] = useState<{ mode: 'create' | 'edit'; officer?: OfficerDto }>();
  const [transferring, setTransferring] = useState<OfficerDto | undefined>();

  const canWrite = officer ? can(officer.accessRole, 'officer.write') : false;
  const canTransfer = officer ? can(officer.accessRole, 'officer.transfer') : false;

  const table = useServerTable<OfficerDto, OfficerSortField>({
    // The SAME tuple the API feeds to `createPaginationSchema`. One allowlist.
    sortFields: OFFICER_SORT_FIELDS,
    defaultSortBy: 'name',
    defaultSortOrder: 'asc',
    filterKeys: ['orgUnitId', 'rankId', 'accessRole'],
    queryKey: (params) => queryKeys.officers.list(params),
    queryFn: (params, signal) =>
      api.get<Paginated<OfficerDto>>('/officers', {
        query: {
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          search: params.search,
          ...params.filters,
        },
        signal,
      }),
  });

  /*
   * [C5c §11] THE UNIT COLUMN'S ELEVATION MARK READS THE TREE THE UNIT FILTER ALREADY FETCHED — the
   * same key and fetcher as `OrgUnitPicker`, so it costs no request. The level is the UNIT's setting
   * (each tree node carries `elevation`); the name it acts at rides on the row as `actingOrgUnit`.
   */
  const orgTree = useQuery({
    queryKey: queryKeys.orgUnits.tree(),
    queryFn: ({ signal }) => fetchScopedOrgTree(signal),
  });
  const elevationByUnit = useMemo(
    () => new Map((orgTree.data?.items ?? []).map((unit) => [unit.id, unit.elevation])),
    [orgTree.data],
  );

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.officers.all });
    // Unit membership changed, so the org screen's counts are stale too — they are keyed
    // under `officers.list`, so this second call covers the tree payload itself.
    void queryClient.invalidateQueries({ queryKey: queryKeys.orgUnits.all });
  };

  const overflowItems = (row: OfficerDto): MenuProps['items'] => [
    {
      key: 'copy-phone',
      label: 'Copy phone number',
      onClick: () => {
        void navigator.clipboard?.writeText(row.phone).then(
          () => message.success('Phone number copied'),
          () => message.error('Could not copy the phone number'),
        );
      },
    },
    {
      key: 'view-unit',
      label: 'View this unit in the organisation',
      disabled: !row.orgUnit,
      // Client-side navigation: `window.location.assign` would drop the whole SPA and
      // reload AntD, the token layer and the session bootstrap to move one screen.
      onClick: () => {
        if (row.orgUnit) void navigate(`/org-tree?unit=${row.orgUnit.id}`);
      },
    },
  ];

  const allColumns: TableProps<OfficerDto>['columns'] = [
    // `key` matches the OFFICER_SORT_FIELDS member exactly — a typo cannot produce a 422
    // because the hook falls back to the default for anything not in the allowlist.
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      defaultSortOrder: 'ascend',
      // [P2] The first column is the way in — it OPENS the officer (read-only without
      // `officer.write`), while `Edit` beside it stays the write action. See `NameCell`.
      // The avatar sits outside the control: it is an illustration, not a second target.
      render: (name: string, row) => (
        <span className="inline-flex min-w-0 items-center gap-2">
          <OfficerAvatar name={name} src={row.photoUrl} className="size-7" />
          <NameCell name={name} onOpen={() => setEditing({ mode: 'edit', officer: row })} />
        </span>
      ),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      // Ten digits, always, and UNFORMATTED. The country code is added at the SMS boundary
      // and nowhere else, and an operator reading a number off a screen to dial it wants
      // exactly the digits they will type.
      render: (phone: string) => <span className="font-mono text-[13px]">{phone}</span>,
    },
    /*
     * The service / collar number is NOT a column.
     *
     * It is still on the DTO, still editable in the drawer, and `serviceNumber` is still
     * in `OFFICER_SORT_FIELDS` — so `?sortBy=serviceNumber` continues to work and the
     * server-side allowlist is unchanged. It is only absent from the list, which is a
     * presentation decision and reversible in one block.
     */
    {
      title: 'Rank',
      key: 'rank',
      render: (_value, row) => row.rank?.shortName ?? <span className="text-subtle">—</span>,
    },
    {
      title: 'Unit',
      key: 'orgUnit',
      render: (_value, row) =>
        row.orgUnit ? (
          <span className="inline-flex items-center gap-2">
            <span className="rounded bg-secondary px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
              {row.orgUnit.shortCode}
            </span>
            <span className="truncate">{row.orgUnit.name}</span>
            <ElevationMark elevation={elevationByUnit.get(row.orgUnitId)} actingAt={row.actingOrgUnit?.name} />
          </span>
        ) : (
          <span className="text-subtle">—</span>
        ),
    },
    {
      title: 'Access',
      dataIndex: 'accessRole',
      key: 'accessRole',
      /* ⚠️ [C4a §8] A PERSON's role, so it is named for THEIR posting — `PS Admin`, `HQ Read`. */
      render: (role: OfficerDto['accessRole'], row: OfficerDto) => (
        <span className={role === 'NONE' ? 'text-sm text-muted-foreground' : 'text-sm'}>
          {accessRoleLabel(role, actingOrgUnitTypeOf(row))}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      // A DOT, not a filled badge. A column of filled badges is a wall of colour in which
      // nothing stands out — the pill is reserved for the one primary state on a detail
      // record.
      render: (active: boolean) =>
        active ? <StatusDot tone="success" label="Active" /> : <StatusDot tone="neutral" label="Inactive" />,
    },
    {
      title: '',
      key: 'actions',
      // [P4-UX] Pinned: an action a scroll can hide is an action that is not there.
      fixed: 'right' as const,
      align: 'right',
      width: 200,
      render: (_value, row) => {
        /*
         * [C5c §11c] An officer posted AT the level the caller acts at is visible and unchangeable.
         * [P7A §7] And ANOTHER super admin is unchangeable without `master_superadmin` — one refusal per
         * row, the elevated one first; both are the server's own sentences. No new `<Tooltip>`, only a
         * title, so the overlay census does not move.
         */
        const refusal = elevatedRefusalAt(officer, row.orgUnitId) ?? superAdminPeerRefusal(officer, row);
        return (
          <div className="flex items-center justify-end gap-1">
            <Tooltip title={canWrite ? refusal : READ_ONLY_TOOLTIP}>
              <Button
                type="text"
                size="small"
                disabled={!canWrite || refusal !== undefined}
                onClick={() => setEditing({ mode: 'edit', officer: row })}
              >
                Edit
              </Button>
            </Tooltip>
            <Tooltip title={canTransfer ? refusal : READ_ONLY_TOOLTIP}>
              <Button
                type="text"
                size="small"
                disabled={!canTransfer || refusal !== undefined}
                onClick={() => setTransferring(row)}
              >
                Transfer
              </Button>
            </Tooltip>
            <Dropdown menu={{ items: overflowItems(row) }} trigger={['click']} placement="bottomRight">
              <Button type="text" size="small" aria-label={`More actions for ${row.name}`}>
                <MoreHorizontal className="size-4" aria-hidden />
              </Button>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  /*
   * ⚠️ THE MENU GETS THE UNFILTERED ARRAY; THE TABLE GETS THE FILTERED ONE. That is the
   * circularity solved — by never letting the two be the same variable. Keys and locks are
   * DERIVED from the array above, so this page keeps no second list of columns.
   */
  const columnKeys = columnKeySet(allColumns ?? []);
  const columnVisibility = useColumnVisibility({ ...columnKeys, storageKey: 'officers' });
  const columns = (allColumns ?? []).filter((column) => columnVisibility.isVisible(String(column.key)));

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        icon={Users}
        title="Officers"
        subtitle={`${table.total} within your scope`}
        actions={
          <Tooltip title={canWrite ? undefined : READ_ONLY_TOOLTIP}>
            <Button type="primary" disabled={!canWrite} onClick={() => setEditing({ mode: 'create' })}>
              New officer
            </Button>
          </Tooltip>
        }
        filters={
          <>
            <FilterField label="Search" className="w-64">
              <Input.Search
                allowClear
                placeholder="Name or phone"
                value={table.searchInput}
                onChange={(event) => table.onSearchChange(event.target.value)}
                data-testid="officer-search"
              />
            </FilterField>
            <FilterField label="Unit" className="w-64">
              <OrgUnitPicker
                allowClear
                /* ⚠️ [C1c §5a] COLLAPSED — expanding 36 divisions over 78 stations is a wall. */
                defaultExpandAll={false}
                placeholder="Any unit in your scope"
                value={table.params.filters.orgUnitId}
                onChange={(value) => table.setFilter('orgUnitId', value)}
              />
            </FilterField>
            <FilterField label="Rank" className="w-56">
              <RankPicker
                allowClear
                value={table.params.filters.rankId}
                onChange={(value) => table.setFilter('rankId', value)}
              />
            </FilterField>
            <FilterField label="Access" className="w-48">
              <Select
                allowClear
                placeholder="Any"
                className="w-full"
                value={table.params.filters.accessRole || undefined}
                onChange={(value: string | undefined) => table.setFilter('accessRole', value)}
                /* ⚠️ [C4a §8] A role as a CATEGORY across every level — it names no node, so the generic words. */
                options={ACCESS_ROLES.map((role) => ({ value: role, label: ACCESS_ROLE_LABEL[role] }))}
              />
            </FilterField>
            {/* The Columns menu sits IN the filter row, not in `actions`: it is a view control,
                the same argument `pages/Arrangements` makes for its Segmented. */}
            <ColumnsMenu
              className="ml-auto"
              columns={allColumns ?? []}
              visibility={columnVisibility}
              locked={columnKeys.locked}
            />
          </>
        }
      />

      <Table<OfficerDto>
        {...table.tableProps}
        columns={columns}
        size="middle"
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: (
            <EmptyState
              size="sm"
              title={
                table.searchInput || Object.keys(table.params.filters).length > 0
                  ? 'No officers match these filters.'
                  : 'No officers in your scope yet.'
              }
            />
          ),
        }}
      />

      {editing && (
        <OfficerDrawer
          open
          mode={editing.mode}
          officer={editing.officer}
          readOnly={
            !canWrite ||
            elevatedRefusalAt(officer, editing.officer?.orgUnitId) !== undefined ||
            /* [P7A §7] Another super admin opens as a VIEW for a viewer who may not change one. */
            superAdminPeerRefusal(officer, editing.officer) !== undefined
          }
          onClose={() => setEditing(undefined)}
          onSuccess={invalidate}
        />
      )}

      {transferring && (
        <TransferOfficerModal
          officer={transferring}
          onClose={() => setTransferring(undefined)}
          onSuccess={invalidate}
        />
      )}
    </div>
  );
};

export default OfficersPage;
